// component/AgentSelector.jsx
// ---------------------------
// Shown when the user has NOT yet chosen an agent.
// Displays a searchable grid of available agents so the user can pick one.
//
// Props:
//   bottheme      – Tailwind class for the header/footer background colour
//   searchTerm    – Current search-input value (controlled)
//   setSearchTerm – Setter to update the search-input value
//   onSelectAgent – Callback called with the agent key when the user clicks a card

import React from 'react';
import agents from '../config/agents';
import { isIdoksNewTheme } from '../utils/hostTheme';

// Line glyphs for the redesign, in place of the pastel emoji. Keyed by agent so each
// card says what it does at a glance; anything not listed falls back to a plain page.
const CARD_MARKS = {
  search_agent: (
    <>
      <path d="M14 3H7.5A2.5 2.5 0 0 0 5 5.5v13A2.5 2.5 0 0 0 7.5 21h9a2.5 2.5 0 0 0 2.5-2.5V8z" />
      <path d="M14 3v5h5" />
      <circle cx="11.2" cy="13.6" r="2.6" />
      <path d="m13.2 15.6 2 2" />
    </>
  ),
  summarizer_agent: (
    <>
      <path d="M14 3H7.5A2.5 2.5 0 0 0 5 5.5v13A2.5 2.5 0 0 0 7.5 21h9a2.5 2.5 0 0 0 2.5-2.5V8z" />
      <path d="M14 3v5h5" />
      <path d="M8.5 12.5h7" />
      <path d="M8.5 15.5h4.5" />
    </>
  ),
  questionpaper_agent: (
    <>
      <path d="M14 3H7.5A2.5 2.5 0 0 0 5 5.5v13A2.5 2.5 0 0 0 7.5 21h9a2.5 2.5 0 0 0 2.5-2.5V8z" />
      <path d="M14 3v5h5" />
      <path d="M10 12.6a1.9 1.9 0 1 1 2.3 1.9v1" />
      <path d="M12.3 17.4h.01" />
    </>
  ),
};

const CardMark = ({ agentKey }) => (
  <svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="currentColor"
    strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {CARD_MARKS[agentKey] || CARD_MARKS.search_agent}
  </svg>
);

const SearchMark = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor"
    strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="11" cy="11" r="6.5" />
    <path d="m16 16 4 4" />
  </svg>
);

const AgentSelector = ({ bottheme, searchTerm, setSearchTerm, onSelectAgent }) => {
  const newTheme = isIdoksNewTheme();

  return (
    <div className="caw-picker flex flex-col h-full justify-between">

      {/* ------------------------------------------------------------------ */}
      {/* Header                                                               */}
      {/* ------------------------------------------------------------------ */}
      <div className={`${bottheme} caw-picker-head to-blue-700 text-white p-5 text-center shadow-md`}>
        <div className="caw-picker-title text-2xl font-bold mb-1">
          {newTheme ? 'iDoks Assistant' : '👋 Welcome to iDoks Assistant'}
        </div>
        <p className="caw-picker-sub text-sm text-blue-100">
          {newTheme ? 'Pick an assistant to work on the files you selected.' : 'Your intelligent workspace helper is here.'}
        </p>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Search bar                                                           */}
      {/* ------------------------------------------------------------------ */}
      <div className="caw-picker-search p-4 bg-white border-b border-gray-200">
        {newTheme && <span className="caw-picker-search-icon"><SearchMark /></span>}
        <input
          type="text"
          placeholder={newTheme ? 'Search assistants' : '🔍 Search agents...'}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50"
        />
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Agent cards grid                                                     */}
      {/* Only agents where isbot !== true are shown (tool agents need files) */}
      {/* ------------------------------------------------------------------ */}
      <div className="caw-picker-grid grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 p-6 bg-white">
        {Object.entries(agents)
          .filter(([id, agent]) =>
            agent.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            agent.description.toLowerCase().includes(searchTerm.toLowerCase())
          )
          .map(([id, agent]) => (
            agent?.isbot !== true && (
              <div
                key={id}
                onClick={() => onSelectAgent(id)}
                className={`caw-picker-card cursor-pointer flex flex-col items-start rounded-2xl shadow-md p-5 transition hover:scale-[1.02] ${newTheme ? '' : agent.color}`}
              >
                <div className="caw-picker-card-head flex items-center space-x-3 mb-2">
                  <span className="caw-picker-card-mark text-2xl">
                    {newTheme ? <CardMark agentKey={id} /> : agent.emoji}
                  </span>
                  <h3 className="caw-picker-card-title text-lg font-semibold">{agent.name}</h3>
                </div>
                <p className="caw-picker-card-desc text-sm opacity-80">{agent.description}</p>
              </div>
            )
          ))}
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Footer                                                               */}
      {/* ------------------------------------------------------------------ */}
      <div className={`${bottheme} caw-picker-foot text-gray-400 text-xs p-3 border-t border-gray-800 text-center space-y-2`}>
        <div className="text-gray-500">
          © {new Date().getFullYear()} <span className="text-white font-semibold">iDoks</span>. All rights reserved.
        </div>
      </div>

    </div>
  );
};

export default AgentSelector;
